import { z } from 'zod';
import { isoDate, uuid } from './common';

export const orgRole = z.enum(['OWNER', 'ADMIN', 'MEMBER']);
export type OrgRole = z.infer<typeof orgRole>;

export const orgMember = z.object({
  userId: uuid,
  email: z.string(),
  name: z.string().nullable(),
  role: orgRole,
  since: isoDate,
  lastActiveAt: isoDate.nullable(),
});
export type OrgMember = z.infer<typeof orgMember>;

/** The workspace, as its settings page and its people list see it. */
export const organization = z.object({
  id: uuid,
  name: z.string(),
  domain: z.string().nullable(),
  /** A workspace-wide notetaker name; null lets each person's own, then "Recording for Name", apply. */
  botDisplayName: z.string().nullable(),
  /**
   * Whether the notetaker may say its few fixed phrases out loud — "Screenshot
   * taken" (PRD 64). Off by default; only an administrator turns it on. It
   * always answers in the chat. Optional only for a page older than the api.
   */
  botVoice: z.boolean().optional(),
  videoRetentionDays: z.number().int(),
  /**
   * Days a meeting's transcript is kept before retention removes the lines —
   * never the note. Null keeps them for as long as the meeting exists.
   */
  transcriptRetentionDays: z.number().int().nullable(),
  /** Whether administrators see the per-person table in analytics. Off by default. */
  peopleAnalytics: z.boolean(),
  /** Whether a connected assistant (MCP) may read this workspace's data. On by default. */
  mcpEnabled: z.boolean(),
  /** What this workspace's knowledge is written in. `auto` follows each meeting. */
  knowledgeLanguage: z.string(),
  /** Whether this person may delete the whole workspace (`canDeleteWorkspace`). */
  canDelete: z.boolean(),
  myRole: orgRole,
  canAdminister: z.boolean(),
  members: z.array(orgMember),
});
export type Organization = z.infer<typeof organization>;

export const updateOrgBody = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  botDisplayName: z.string().trim().min(1).max(80).nullable().optional(),
  botVoice: z.boolean().optional(),
  videoRetentionDays: z.number().int().min(1).max(3650).optional(),
  /** Null to keep transcripts; at least a week, so a setting cannot eat a meeting before anyone read it. */
  transcriptRetentionDays: z.number().int().min(7).max(3650).nullable().optional(),
  peopleAnalytics: z.boolean().optional(),
  mcpEnabled: z.boolean().optional(),
  knowledgeLanguage: z.string().min(2).max(16).optional(),
});
export type UpdateOrgBody = z.infer<typeof updateOrgBody>;

/**
 * The two pictures the notetaker shows on its camera tile in a call
 * (2026-09-30): while it records, and while it is paused. A workspace may put
 * its own in place of the defaults — Vladimir: *"add the solution to change
 * these images to the corporate ones inside the organization settings"*.
 */
export const tileKind = z.enum(['recording', 'paused']);
export type TileKind = z.infer<typeof tileKind>;

const tile = z.object({
  /** The workspace's own, rather than Ptitim's default. */
  custom: z.boolean(),
  /** The picture itself, as a `data:image/jpeg;base64,…` URL. */
  image: z.string(),
});
export const orgTiles = z.object({ recording: tile, paused: tile });
export type OrgTiles = z.infer<typeof orgTiles>;

export const setTileBody = z.object({
  /**
   * A 1280×720 JPEG under 1.3 MB — Recall's rules, which the page fits the
   * chosen picture to — as base64 or a data URL.
   */
  image: z.string().min(16).max(2_000_000),
});
export type SetTileBody = z.infer<typeof setTileBody>;

/** Ownership is handed on, never assigned — so a role change names only these two. */
export const setOrgRoleBody = z.object({ role: z.enum(['ADMIN', 'MEMBER']) });
export type SetOrgRoleBody = z.infer<typeof setOrgRoleBody>;

/**
 * One act, as an administrator reads it (checklist B7).
 *
 * ⚠ There is no field here that can carry a sentence anybody said. `detail`
 * holds the shape of the act — a role, a link setting, how many meetings went —
 * and that is what lets an administrator read their workspace's log without
 * reading their colleagues' meetings.
 */
export const auditEvent = z.object({
  at: isoDate,
  /** Who did it, by the address they had at the time. */
  actorEmail: z.string(),
  action: z.string(),
  subject: z.enum(['meeting', 'account', 'workspace', 'space', 'person']),
  subjectId: z.string().nullable(),
  /** The person it was about, when it was about somebody. */
  targetEmail: z.string().nullable(),
  detail: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
});
export type AuditEvent = z.infer<typeof auditEvent>;
